/**
 * Ribbon trails.
 *
 * A projectile drawn as a dot is a dot. The same projectile with a tail of
 * history behind it, tapering in width and burning out in intensity, reads as
 * speed — and the taper is what does it, not the length. A constant-width
 * streak looks like a scratch on the lens.
 *
 * Length is bounded in *pixels of arc*, not in samples. A fixed sample count
 * makes ribbon length a direct readout of projectile speed, which at 900px/s
 * paints a 360px scratch across a 640px playfield and at 60px/s leaves a stub.
 *
 * Implementation notes:
 *  - Every trail owns a fixed slot: `segments` ring-buffered points, two
 *    vertices per point, and a pre-built index block. Nothing is rebuilt at
 *    runtime except vertex data, so the whole batch is one draw call and the
 *    index buffer is uploaded exactly once.
 *  - Unused points collapse onto the tail with zero width, which degenerates
 *    their triangles to nothing. Cheaper than re-indexing every frame.
 *  - Teleports are checked on push. A homing projectile that gets recycled
 *    across the map would otherwise draw a bright line through the playfield,
 *    which is the single most obvious way a ribbon system gives itself away.
 */

import * as THREE from 'three';
import { PARTICLES } from '@/core/config';
import { perf } from '@/core/perf';
import { INTENSITY, PHOSPHOR } from '@/core/palette';
import type { PhosphorName } from '@/core/palette';
import type { Enemy, RenderSystem, System, World } from '@/core/types';

const FREE = 0;
const ACTIVE = 1;
const DYING = 2;

export interface TrailSystemOptions {
  maxTrails?: number;
  segments?: number;
}

export class TrailSystem implements System, RenderSystem {
  readonly name = 'trails';

  /** Add this to the scene. One indexed draw call for every ribbon. */
  readonly object: THREE.Mesh;

  private readonly maxTrails: number;
  private readonly segs: number;

  // ── ring buffers ──
  private readonly ptX: Float32Array;
  private readonly ptY: Float32Array;
  /** Index of the newest point inside each slot's ring. */
  private readonly head: Int32Array;
  private readonly count: Int32Array;
  private readonly state: Uint8Array;
  /** Retire fade, 1 → 0 after `release`. */
  private readonly fade: Float32Array;

  // ── per-trail appearance ──
  private readonly cr: Float32Array;
  private readonly cg: Float32Array;
  private readonly cb: Float32Array;
  private readonly gain: Float32Array;
  private readonly width: Float32Array;
  /** Arc-length bound for this slot, in buffer pixels. */
  private readonly lenLimit: Float32Array;

  // ── slot allocation ──
  private readonly freeList: Int32Array;
  private freeTop: number;
  private high = -1;

  // ── GPU buffers ──
  private readonly vertArr: Float32Array;
  private readonly colArr: Float32Array;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;

  /** Jump distance in buffer pixels that restarts a ribbon instead of drawing it. */
  teleportPx = 48;
  /**
   * Longest ribbon in buffer pixels, measured along the path rather than in
   * samples. A fixed sample count makes length a proxy for projectile tier: at
   * the tier-4 sniper's 900px/s, 24 sim steps of history is 360px of live
   * ribbon across a 640px playfield — a scratch over half the screen — while
   * the tier-1 turret's 60px/s leaves a 24px stub. Bounding the arc gives fast
   * rounds a short bright dart and slow rounds their full ribbon.
   */
  maxLengthPx = 110;
  /** Seconds a released trail keeps drawing while it burns out. */
  retireSeconds = 0.18;
  /**
   * Pull the head point from `world.projectiles` each step. Set false if the
   * owning subsystem drives `push` itself.
   */
  followProjectiles = true;
  /**
   * Give every flocking enemy a ribbon of its own. In every Geometry Wars swarm
   * reference the bodies smear, and a 460-body cloud of hard dots is the tell
   * that separates a particle field from a swarm. Slots are allocated and
   * retired here rather than in the enemy sim, which owns no render state.
   */
  followFlockers = true;
  /** Ribbon width for a swarm body. Thinner than a projectile by design. */
  flockWidth = 1.5;
  /** Gain multiplier for a swarm body. Low: 460 of these must not blow out. */
  flockGain = 0.42;
  /**
   * Arc bound for a swarm body, in buffer pixels. Roughly four body lengths —
   * enough to read as motion, short enough that the cloud does not smear into
   * one continuous sheet of light.
   */
  flockLengthPx = 13;
  /** Trails a swarm may hold at once, leaving headroom for projectiles. */
  private readonly flockCap: number;

  /**
   * The live swarm ribbon budget.
   *
   * 460 ribbons is 460 index blocks of vertex work every frame, which makes
   * this the largest single lever the render side has that costs no gameplay
   * information at all: a projectile keeps its ribbon whatever happens, because
   * a shot with no tail is a shot the player cannot read.
   */
  private get flockBudget(): number {
    return Math.floor(this.flockCap * perf.caps.trailScale);
  }

  // ── flocker slot bookkeeping ──
  private readonly flockOwner: (Enemy | null)[];
  private readonly flockOwnerId: Int32Array;
  private readonly flockSlot: Int32Array;
  private flockCount = 0;
  /** Highest enemy id seen. Ids are monotonic, so anything above it is new. */
  private lastEnemyId = 0;

  constructor(opts: TrailSystemOptions = {}) {
    this.maxTrails = Math.max(1, opts.maxTrails ?? PARTICLES.maxTrails);
    this.segs = Math.max(2, opts.segments ?? PARTICLES.trailSegments);

    const t = this.maxTrails;
    const pts = t * this.segs;

    this.ptX = new Float32Array(pts);
    this.ptY = new Float32Array(pts);
    this.head = new Int32Array(t);
    this.count = new Int32Array(t);
    this.state = new Uint8Array(t);
    this.fade = new Float32Array(t);
    this.cr = new Float32Array(t);
    this.cg = new Float32Array(t);
    this.cb = new Float32Array(t);
    this.gain = new Float32Array(t);
    this.width = new Float32Array(t);
    this.lenLimit = new Float32Array(t);

    this.flockCap = Math.floor(t * 0.6);
    this.flockOwner = new Array(t).fill(null);
    this.flockOwnerId = new Int32Array(t);
    this.flockSlot = new Int32Array(t);

    this.freeList = new Int32Array(t);
    for (let i = 0; i < t; i++) this.freeList[i] = t - 1 - i;
    this.freeTop = t;

    const verts = pts * 2;
    this.vertArr = new Float32Array(verts * 3);
    // rgb carries the already-scaled HDR colour, a carries the -1..1 side so the
    // fragment stage can round the ribbon off across its width.
    this.colArr = new Float32Array(verts * 4);

    this.posAttr = new THREE.BufferAttribute(this.vertArr, 3);
    this.colAttr = new THREE.BufferAttribute(this.colArr, 4);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);

    // Index block per slot, built once. Two triangles bridge each pair of
    // consecutive ribbon points.
    const quads = t * (this.segs - 1);
    const idx = verts > 65535 ? new Uint32Array(quads * 6) : new Uint16Array(quads * 6);
    let o = 0;
    for (let s = 0; s < t; s++) {
      const base = s * this.segs * 2;
      for (let k = 0; k < this.segs - 1; k++) {
        const a = base + k * 2;
        idx[o++] = a;
        idx[o++] = a + 1;
        idx[o++] = a + 2;
        idx[o++] = a + 2;
        idx[o++] = a + 1;
        idx[o++] = a + 3;
      }
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('aColour', this.colAttr);
    this.geometry.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geometry.setDrawRange(0, 0);
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.material = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: TRAIL_VERT,
      fragmentShader: TRAIL_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      blendEquation: THREE.AddEquation,
    });

    this.object = new THREE.Mesh(this.geometry, this.material);
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = false;
    // Under the particles, over the lattice.
    this.object.renderOrder = 30;
    this.object.name = 'trails';
  }

  get activeCount(): number {
    return this.maxTrails - this.freeTop;
  }

  // ───────────────────────────────────────────────────────────── allocation ──

  /**
   * Takes a slot. Returns -1 when the pool is exhausted; callers must handle it.
   *
   * The default gain is above 1 on purpose: a shot crossing the field is one of
   * the few sources of light a tower defense has *off* the corridor, and at
   * unity a yellow ribbon wrote 2.2 and skirted by three pixels. Swarm ribbons
   * pass `flockGain` and are unaffected.
   */
  acquire(colour: PhosphorName = 'white', width = 3, gain = 1.7, maxLength = 0): number {
    if (this.freeTop <= 0) return -1;
    const id = this.freeList[--this.freeTop];
    const c = PHOSPHOR[colour];
    this.lenLimit[id] = maxLength > 0 ? maxLength : this.maxLengthPx;
    this.state[id] = ACTIVE;
    this.count[id] = 0;
    this.head[id] = 0;
    this.fade[id] = 1;
    this.cr[id] = c[0];
    this.cg[id] = c[1];
    this.cb[id] = c[2];
    this.gain[id] = INTENSITY[colour] * gain;
    this.width[id] = width;
    if (id > this.high) this.high = id;
    return id;
  }

  /** Hands the slot back. The ribbon keeps drawing while it burns out. */
  release(id: number): void {
    if (id < 0 || id >= this.maxTrails) return;
    if (this.state[id] !== ACTIVE) return;
    this.state[id] = DYING;
    this.fade[id] = 1;
  }

  /** Restyles a live trail, e.g. when a projectile crits. */
  setColour(id: number, colour: PhosphorName, gain = 1): void {
    if (id < 0 || id >= this.maxTrails || this.state[id] === FREE) return;
    const c = PHOSPHOR[colour];
    this.cr[id] = c[0];
    this.cg[id] = c[1];
    this.cb[id] = c[2];
    this.gain[id] = INTENSITY[colour] * gain;
  }

  /** Appends a point. A jump past `teleportPx` restarts the ribbon. */
  push(id: number, x: number, y: number): void {
    if (id < 0 || id >= this.maxTrails || this.state[id] !== ACTIVE) return;
    const base = id * this.segs;
    const n = this.count[id];

    if (n > 0) {
      const h = base + this.head[id];
      const dx = x - this.ptX[h];
      const dy = y - this.ptY[h];
      const jump = this.teleportPx;
      if (dx * dx + dy * dy > jump * jump) {
        // Pool recycling or a warp. Drawing through it would streak the screen.
        this.count[id] = 0;
      } else if (dx * dx + dy * dy < 0.0004) {
        // Stationary: do not burn ring slots, the ribbon would collapse to a dot.
        return;
      }
    }

    const next = this.count[id] === 0 ? 0 : (this.head[id] + 1) % this.segs;
    this.head[id] = next;
    this.ptX[base + next] = x;
    this.ptY[base + next] = y;
    if (this.count[id] < this.segs) this.count[id]++;
  }

  clear(): void {
    this.state.fill(FREE);
    this.count.fill(0);
    for (let i = 0; i < this.maxTrails; i++) this.freeList[i] = this.maxTrails - 1 - i;
    this.freeTop = this.maxTrails;
    this.high = -1;
    for (let k = 0; k < this.flockCount; k++) this.flockOwner[k] = null;
    this.flockCount = 0;
    // Ids only ever climb, so zeroing the mark re-adopts everything currently
    // alive on the next step rather than stranding it without a ribbon.
    this.lastEnemyId = 0;
    this.geometry.setDrawRange(0, 0);
  }

  // ─────────────────────────────────────────────────────────────────── step ──

  step(dt: number, world: World): void {
    if (this.followProjectiles) {
      const list = world.projectiles;
      for (let i = 0; i < list.length; i++) {
        const p = list[i];
        if (p.alive && p.trail >= 0) this.push(p.trail, p.pos.x, p.pos.y);
      }
    }

    if (this.followFlockers) this.stepFlockers(world);

    const decay = dt / Math.max(1e-3, this.retireSeconds);
    for (let id = 0; id <= this.high; id++) {
      if (this.state[id] !== DYING) continue;
      this.fade[id] -= decay;
      if (this.fade[id] <= 0) {
        this.state[id] = FREE;
        this.count[id] = 0;
        this.freeList[this.freeTop++] = id;
        if (id === this.high) {
          let h = this.high;
          while (h >= 0 && this.state[h] === FREE) h--;
          this.high = h;
        }
      }
    }
  }

  /**
   * Keeps one ribbon per flocking enemy, without the enemy sim having to know
   * this system exists and without a per-frame set membership test.
   *
   * Adoption leans on two guarantees the enemy pool already makes: ids are
   * handed out monotonically, so anything above the high-water mark is new this
   * step; and objects are recycled, so identity is the object *and* its id.
   */
  private stepFlockers(world: World): void {
    for (let k = this.flockCount - 1; k >= 0; k--) {
      const e = this.flockOwner[k];
      if (e !== null && e.alive && e.id === this.flockOwnerId[k]) {
        this.push(this.flockSlot[k], e.pos.x, e.pos.y);
        continue;
      }
      this.release(this.flockSlot[k]);
      const last = --this.flockCount;
      this.flockOwner[k] = this.flockOwner[last];
      this.flockOwnerId[k] = this.flockOwnerId[last];
      this.flockSlot[k] = this.flockSlot[last];
      this.flockOwner[last] = null;
    }

    // A rung that cuts the budget has to give the slots back now, not wait for
    // the swarm currently holding them to die. Ribbons burn out over
    // `retireSeconds` either way, so the cut reads as a fade rather than a pop.
    const budget = this.flockBudget;
    while (this.flockCount > budget) {
      const k = --this.flockCount;
      this.release(this.flockSlot[k]);
      this.flockOwner[k] = null;
    }

    const list = world.enemies;
    const mark = this.lastEnemyId;
    let highest = mark;
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (e.id > highest) highest = e.id;
      if (e.id <= mark || !e.alive || !e.archetype.flocks) continue;
      if (this.flockCount >= budget) break;
      const id = this.acquire(
        e.archetype.colour, this.flockWidth, this.flockGain, this.flockLengthPx,
      );
      if (id < 0) break;
      const k = this.flockCount++;
      this.flockOwner[k] = e;
      this.flockOwnerId[k] = e.id;
      this.flockSlot[k] = id;
      this.push(id, e.pos.x, e.pos.y);
    }
    this.lastEnemyId = highest;
  }

  // ─────────────────────────────────────────────────────────────────── sync ──

  sync(_world: World, _alpha: number): void {
    const used = this.high + 1;
    if (used <= 0) {
      this.geometry.setDrawRange(0, 0);
      return;
    }
    for (let id = 0; id < used; id++) this.writeRibbon(id);

    const vertexFloats = used * this.segs * 2;
    this.posAttr.clearUpdateRanges();
    this.posAttr.addUpdateRange(0, vertexFloats * 3);
    this.posAttr.needsUpdate = true;
    this.colAttr.clearUpdateRanges();
    this.colAttr.addUpdateRange(0, vertexFloats * 4);
    this.colAttr.needsUpdate = true;

    this.geometry.setDrawRange(0, used * (this.segs - 1) * 6);
  }

  /**
   * Expands one ring buffer into a strip of paired vertices. `k` walks from the
   * head backwards, so `u` runs 0 at the projectile to 1 at the oldest point.
   */
  private writeRibbon(id: number): void {
    const segs = this.segs;
    const vBase = id * segs * 2;
    const cnt = this.state[id] === FREE ? 0 : this.count[id];

    if (cnt < 2) {
      // Collapse the whole slot. Zero-area triangles cost a vertex shader
      // invocation and nothing else.
      const stop = vBase + segs * 2;
      for (let v = vBase; v < stop; v++) {
        const o = v * 3;
        this.vertArr[o] = 0;
        this.vertArr[o + 1] = 0;
        this.vertArr[o + 2] = 0;
        const c = v * 4;
        this.colArr[c] = 0;
        this.colArr[c + 1] = 0;
        this.colArr[c + 2] = 0;
        this.colArr[c + 3] = 0;
      }
      return;
    }

    const base = id * segs;
    const h = this.head[id];
    const w0 = this.width[id];
    const g = this.gain[id] * (this.state[id] === DYING ? this.fade[id] : 1);
    const r = this.cr[id];
    const gr = this.cg[id];
    const b = this.cb[id];
    const invSeg = 1 / (segs - 1);
    const limit = this.lenLimit[id] > 0 ? this.lenLimit[id] : this.maxLengthPx;
    const invLen = 1 / Math.max(1e-3, limit);

    let lastX = 0;
    let lastY = 0;
    /** Arc distance walked back from the head, in buffer pixels. */
    let arc = 0;
    let prevX = 0;
    let prevY = 0;

    for (let k = 0; k < segs; k++) {
      const ki = k < cnt ? k : cnt - 1;
      const i = base + ((h - ki + segs * 2) % segs);
      const x = this.ptX[i];
      const y = this.ptY[i];

      if (k > 0 && k < cnt) arc += Math.hypot(x - prevX, y - prevY);
      prevX = x;
      prevY = y;

      const live = k < cnt && arc < limit;

      // Direction from the neighbours: newer minus older, so the normal is
      // stable through a curve instead of flipping on every sample.
      const iPrev = base + ((h - Math.max(0, ki - 1) + segs * 2) % segs);
      const iNext = base + ((h - Math.min(cnt - 1, ki + 1) + segs * 2) % segs);
      let dx = this.ptX[iPrev] - this.ptX[iNext];
      let dy = this.ptY[iPrev] - this.ptY[iNext];
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 1e-5) {
        dx /= len;
        dy /= len;
      } else {
        dx = 1;
        dy = 0;
      }
      const nxv = -dy;
      const nyv = dx;

      // Taper on whichever runs out first, samples or arc length. Tapering on
      // the sample index alone would leave a fast ribbon cut off at 70% width
      // where the arc bound bites, which reads as a broken polygon.
      let t = k * invSeg;
      const byArc = arc * invLen;
      if (byArc > t) t = byArc;
      const taper = live && t < 1 ? 1 - t : 0;
      const halfW = w0 * 0.5 * taper;
      // Intensity falls faster than width: the head should be the only part
      // that clears the bloom threshold.
      const inten = live ? g * taper * taper : 0;

      if (live) {
        lastX = x;
        lastY = y;
      }
      const px = live ? x : lastX;
      const py = live ? y : lastY;

      const vL = vBase + k * 2;
      const oL = vL * 3;
      this.vertArr[oL] = px - nxv * halfW;
      this.vertArr[oL + 1] = py - nyv * halfW;
      this.vertArr[oL + 2] = 0;
      this.vertArr[oL + 3] = px + nxv * halfW;
      this.vertArr[oL + 4] = py + nyv * halfW;
      this.vertArr[oL + 5] = 0;

      const cL = vL * 4;
      this.colArr[cL] = r * inten;
      this.colArr[cL + 1] = gr * inten;
      this.colArr[cL + 2] = b * inten;
      this.colArr[cL + 3] = -1;
      this.colArr[cL + 4] = r * inten;
      this.colArr[cL + 5] = gr * inten;
      this.colArr[cL + 6] = b * inten;
      this.colArr[cL + 7] = 1;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

const TRAIL_VERT = /* glsl */ `
precision highp float;

attribute vec4 aColour;   // rgb = HDR colour, a = side across the ribbon

varying vec3 vColour;
varying float vSide;

void main() {
  vColour = aColour.rgb;
  vSide = aColour.a;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const TRAIL_FRAG = /* glsl */ `
precision highp float;

varying vec3 vColour;
varying float vSide;

void main() {
  // Parabolic across the width: a bright spine with a soft shoulder, which is
  // what survives the bloom pass as a line rather than a slab.
  float a = abs(vSide);
  float m = 1.0 - a * a;
  if (m <= 0.002) discard;
  gl_FragColor = vec4(vColour * m, 1.0);
}
`;
